import {ScrollArea as BaseScrollArea} from '@base-ui/react/scroll-area';
import type {ComponentPropsWithoutRef, ReactNode, RefObject} from 'react';

import {twx} from '@/utils/tailwindUtils';

type ScrollAreaProps = Omit<ComponentPropsWithoutRef<typeof BaseScrollArea.Root>, 'children'> & {
    viewportClassName?: string;
    contentClassName?: string;
    viewportRef?: RefObject<HTMLDivElement | null>;
    children?: ReactNode;
    ref?: React.Ref<HTMLDivElement>;
};

const ScrollArea = ({
    className,
    children,
    viewportRef,
    viewportClassName,
    contentClassName,
    ref,
    ...props
}: ScrollAreaProps) => (
    <BaseScrollArea.Root ref={ref} className={twx('relative overflow-hidden', className)} {...props}>
        <BaseScrollArea.Viewport
            className={twx('h-full w-full rounded-[inherit]', viewportClassName)}
            ref={viewportRef}
        >
            <BaseScrollArea.Content className={contentClassName}>{children}</BaseScrollArea.Content>
        </BaseScrollArea.Viewport>
        <ScrollBar />
        <BaseScrollArea.Corner />
    </BaseScrollArea.Root>
);

type ScrollBarProps = ComponentPropsWithoutRef<typeof BaseScrollArea.Scrollbar> & {
    orientation?: 'vertical' | 'horizontal';
    ref?: React.Ref<HTMLDivElement>;
};

const ScrollBar = ({className, orientation = 'vertical', ref, ...props}: ScrollBarProps) => (
    <BaseScrollArea.Scrollbar
        ref={ref}
        orientation={orientation}
        className={twx(
            'z-10 flex touch-none select-none transition-colors',
            orientation === 'vertical' && 'h-full w-1',
            orientation === 'horizontal' && 'h-1 flex-col',
            className
        )}
        {...props}
    >
        <BaseScrollArea.Thumb className='relative flex-1 rounded-full bg-tips-scrollbar-thumb' />
    </BaseScrollArea.Scrollbar>
);

export {ScrollArea, ScrollBar};
